// @flow
import { type ActionType, createAction, handleActions } from 'redux-actions';
import produce from 'immer';
import { applyPenders } from 'redux-pender';
import * as QuotesAPI from 'lib/api/quotes';

const GET_RECENT_QUOTES = 'quoting/GET_RECENT_QUOTES';
const PREFETCH_RECENT_QUOTES = 'quoting/PREFETCH';
const REVEAL_PREFETCHED = 'quoting/REVEAL_PREFETCHED';
const CLEAR_USER_QUOTES = 'quoting/CLEAR_USER_QUOTES';
const GET_USER_QUOTES = 'quoting/GET_USER_QUOTES';
const PREFETCH_USER_QUOTES = 'quoting/PREFETCH_USER_QUOTES';
const GET_TRENDING_QUOTES = 'quoting/GET_TRENDING_QUOTES';
const PREFETCH_TRENDING_QUOTES = 'quoting/PREFETCH_TRENDING_QUOTES';
const GET_TAG_QUOTES = 'quoting/GET_TAG_QUOTES';
const PREFETCH_TAG_QUOTES = 'quoting/PREFETCH_TAG_QUOTES';
const GET_CATEGORY_QUOTES = 'quoting/GET_CATEGORY_QUOTES';
const PREFETCH_CATEGORY_QUOTES = 'quoting/PREFETCH_CATEGORY_QUOTES';
const CLEAR_CATEGORY_QUOTES = 'quoting/CLEAR_CATEGORY_QUOTES';
const CLEAR_TAG_QUOTES = 'quoting/CLEAR_TAG_QUOTES';
const GET_TEMP_QUOTES = 'quoting/GET_TEMP_QUOTES';
const PREFETCH_TEMP_QUOTES = 'quoting/PREFETCH_TEMP_QUOTES';
const REMOVE_TEMP_QUOTE = 'quoting/REMOVE_TEMP_QUOTE';
const FLUSH_LIST = 'quoting/FLUSH_LIST';

// potential improvement :: remove duplicates
export const actionCreators = {
  getRecentQuotes: createAction(GET_RECENT_QUOTES, QuotesAPI.getPublicQuotes),
  prefetchRecentQuotes: createAction(PREFETCH_RECENT_QUOTES, QuotesAPI.getPublicQuotes),
  revealPrefetched: createAction(REVEAL_PREFETCHED, (type: string) => type),
  clearUserQuotes: createAction(CLEAR_USER_QUOTES),
  getUserQuotes: createAction(
    GET_USER_QUOTES,
    QuotesAPI.getUserQuotes,
    (meta: { username: string, tag?: string }) => meta,
  ),
  prefetchUserQuotes: createAction(PREFETCH_USER_QUOTES, QuotesAPI.getUserQuotes),
  getTrendingQuotes: createAction(GET_TRENDING_QUOTES, QuotesAPI.getTrendingQuotes),
  prefetchTrendingQuotes: createAction(PREFETCH_TRENDING_QUOTES, QuotesAPI.getTrendingQuotes),
  getTagQuotes: createAction(
    GET_TAG_QUOTES,
    QuotesAPI.getPublicQuotesByTag,
    (meta: { tag: string }) => meta,
  ),
  prefetchTagQuotes: createAction(PREFETCH_TAG_QUOTES, QuotesAPI.getPublicQuotesByTag),
  getCategoryQuotes: createAction(
    GET_CATEGORY_QUOTES,
    QuotesAPI.getPublicQuotesByCategory,
    (meta: { category: string, sort?: string }) => meta,
  ),
  prefetchCategoryQuotes: createAction(
    PREFETCH_CATEGORY_QUOTES,
    QuotesAPI.getPublicQuotesByCategory,
  ),
  clearCategoryQuotes: createAction(CLEAR_CATEGORY_QUOTES),
  clearTagQuotes: createAction(CLEAR_TAG_QUOTES),
  getTempQuotes: createAction(GET_TEMP_QUOTES, QuotesAPI.getTempQuotes),
  prefetchTempQuotes: createAction(PREFETCH_TEMP_QUOTES, QuotesAPI.getTempQuotes),
  removeTempQuote: createAction(REMOVE_TEMP_QUOTE, (quoteId: string) => quoteId),
  flushList: createAction(FLUSH_LIST),
};

export type QuoteItem = {
  id: string,
  title: string,
  body: string,
  thumbnail: string,
  is_markdown: boolean,
  is_private: boolean,
  released_at: string,
  created_at: string,
  updated_at: string,
  tags: string[],
  categories: any[],
  url_slug: string,
  likes: number,
  comments_count: number,
  user: {
    id: string,
    username: string,
    quoting_name: string,
    short_bio: string,
    thumbnail: ?string,
  },
  meta: any,
};

export type QuotingSet = {
  quotes: ?(QuoteItem[]),
  prefetched: ?(QuoteItem[]),
  end: boolean,
};

export type Quoting = {
  trendingMap: {
    [id: string]: boolean,
  },
  trending: QuotingSet,
  recent: QuotingSet,
  user: QuotingSet & { currentUsername: ?string, currentTag: ?string },
  tag: QuotingSet & { currentTag: ?string },
  category: QuotingSet & { currentCategory: ?string },
  temp: QuotingSet,
};

type RevealPrefetchedAction = ActionType<typeof actionCreators.revealPrefetched>;
type QuotesResponseAction = {
  type: string,
  payload: {
    data: QuoteItem[],
    category?: ?string,
    sort?: ?string,
  },
  meta: any,
};
type RemoveTempQuoteAction = ActionType<typeof actionCreators.removeTempQuote>;

export interface QuotingActionCreators {
  getRecentQuotes(cursor: string, category: string, sort: string): any;

  prefetchRecentQuotes(cursor: string, category: string, sort: string): any;

  revealPrefetched(type: string): any;

  getUserQuotes(payload: QuotesAPI.GetUserQuotesPayload): any;

  prefetchUserQuotes(payload: QuotesAPI.GetUserQuotesPayload): any;

  getTrendingQuotes(): any;

  prefetchTrendingQuotes(offset: number): any;

  getTagQuotes(payload: QuotesAPI.GetPublicQuotesByTagPayload): any;

  prefetchTagQuotes(payload: QuotesAPI.GetPublicQuotesByTagPayload): any;

  getCategoryQuotes(payload: QuotesAPI.GetPublicQuotesByCategoryPayload): any;

  prefetchCategoryQuotes(payload: QuotesAPI.GetPublicQuotesByCategoryPayload): any;

  getTempQuotes(payload: QuotesAPI.GetTempQuotesPayload): any;

  prefetchTempQuotes(payload: QuotesAPI.GetTempQuotesPayload): any;

  clearCategoryQuotes(): any;

  clearTagQuotes(): any;

  clearUserQuotes(): any;

  removeTempQuote(quoteId: string): any;

  flushList(): any;
}

const initialQuotingSet = {
  quotes: null,
  prefetched: null,
  end: false,
};

const initialState: Quoting = {
  trendingMap: {},
  trending: initialQuotingSet,
  recent: initialQuotingSet,
  user: { ...initialQuotingSet, currentUsername: null, currentTag: null },
  tag: { ...initialQuotingSet, currentTag: null },
  category: { ...initialQuotingSet, currentCategory: null },
  temp: initialQuotingSet,
};

const reducer = handleActions(
  {
    [REVEAL_PREFETCHED]: (state, action: RevealPrefetchedAction) => {
      return produce(state, draft => {
        const { payload } = action;
        const { quotes, prefetched } = draft[payload];
        if (quotes && prefetched) {
          quotes.push(...prefetched);
          draft[payload].prefetched = null;
        }
      });
    },
    [CLEAR_USER_QUOTES]: state => {
      return produce(state, draft => {
        draft.user = {
          quotes: null,
          prefetched: null,
          end: false,
          currentUsername: null,
          currentTag: null,
        };
      });
    },
    [CLEAR_TAG_QUOTES]: state => {
      return produce(state, draft => {
        draft.tag = {
          quotes: null,
          prefetched: null,
          end: false,
          currentTag: null,
        };
      });
    },
    [CLEAR_CATEGORY_QUOTES]: state => {
      return produce(state, draft => {
        draft.category = {
          quotes: null,
          prefetched: null,
          end: false,
          currentCategory: null,
        };
      });
    },
    [REMOVE_TEMP_QUOTE]: (state, { payload }: RemoveTempQuoteAction) => {
      return produce(state, draft => {
        if (!draft.temp.quotes) return;
        draft.temp.quotes = draft.temp.quotes.filter(p => p.id !== payload);
      });
    },
    [FLUSH_LIST]: state => {
      return initialState;
    },
  },
  initialState,
);

export default applyPenders(reducer, [
  {
    type: GET_RECENT_QUOTES,
    onSuccess: (state: Quoting, action: QuotesResponseAction) => {
      const { category, sort } = action.payload;
      return produce(state, draft => {
        draft.recent = {
          category,
          sort,
          end: false,
          quotes: action.payload.data,
          prefetched: null,
        };
      });
    },
  },
  {
    type: PREFETCH_RECENT_QUOTES,
    onSuccess: (state: Quoting, action: QuotesResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.recent.prefetched = data;
        if (data && data.length === 0) {
          draft.recent.end = true;
        }
      });
    },
  },
  {
    type: GET_TAG_QUOTES,
    onSuccess: (state: Quoting, action: QuotesResponseAction) => {
      return produce(state, draft => {
        draft.tag = {
          end: action.payload.data.length < 20,
          quotes: action.payload.data,
          prefetched: null,
          currentTag: action.meta.tag,
        };
      });
    },
  },
  {
    type: PREFETCH_TAG_QUOTES,
    onSuccess: (state: Quoting, action: QuotesResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.tag.prefetched = data;
        if (data && data.length === 0) {
          draft.tag.end = true;
        }
      });
    },
  },
  {
    type: GET_CATEGORY_QUOTES,
    onSuccess: (state: Quoting, action: QuotesResponseAction) => {
      return produce(state, draft => {
        draft.category = {
          end: action.payload.data.length < 20,
          quotes: action.payload.data,
          prefetched: null,
          currentCategory: action.meta.category,
          sort: action.meta.sort,
        };
      });
    },
  },
  {
    type: PREFETCH_CATEGORY_QUOTES,
    onSuccess: (state: Quoting, action: QuotesResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.category.prefetched = data;
        if (data && data.length === 0) {
          draft.category.end = true;
        }
      });
    },
  },
  {
    type: GET_TRENDING_QUOTES,
    onSuccess: (state: Quoting, action: QuotesResponseAction) => {
      return produce(state, draft => {
        action.payload.data.forEach(quote => {
          draft.trendingMap[quote.id] = true;
        });
        draft.trending = {
          end: false,
          quotes: action.payload.data,
          prefetched: null,
        };
      });
    },
  },
  {
    type: PREFETCH_TRENDING_QUOTES,
    onSuccess: (state: Quoting, action: QuotesResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        const filtered = data.filter(quote => !state.trendingMap[quote.id]);
        draft.trending.prefetched = filtered;
        filtered.forEach(quote => {
          draft.trendingMap[quote.id] = true;
        });
        if (data && data.length === 0) {
          draft.trending.end = true;
        }
      });
    },
  },
  {
    type: GET_USER_QUOTES,
    onPending: (state: Quoting) => {
      return produce(state, draft => {
        draft.user.end = false;
        draft.user.prefetched = null;
      });
    },
    onSuccess: (state: Quoting, action: QuotesResponseAction) => {
      return produce(state, draft => {
        draft.user.quotes = action.payload.data;
        draft.user.currentUsername = action.meta.username;
        if (action.meta.tag) {
          draft.user.currentTag = action.meta.tag;
        }
        if (action.payload.data.length < 20) {
          draft.user.end = true;
        }
      });
    },
  },
  {
    type: PREFETCH_USER_QUOTES,
    onSuccess: (state: Quoting, action: QuotesResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.user.prefetched = data;
        if (data && data.length === 0) {
          draft.user.end = true;
        }
      });
    },
  },
  {
    type: GET_TEMP_QUOTES,
    onPending: (state: Quoting) => {
      return produce(state, draft => {
        draft.temp.end = false;
        draft.temp.prefetched = null;
      });
    },
    onSuccess: (state: Quoting, action: QuotesResponseAction) => {
      return produce(state, draft => {
        draft.temp.quotes = action.payload.data;
        if (action.payload.data.length < 20) {
          draft.temp.end = true;
        }
      });
    },
  },
  {
    type: PREFETCH_TEMP_QUOTES,
    onSuccess: (state: Quoting, action: QuotesResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.temp.prefetched = data;
        if (data && data.length === 0) {
          draft.temp.end = true;
        }
      });
    },
  },
]);



// WEBPACK FOOTER //
// ./src/store/modules/quoting.js